/**
 * Request Queue - Limits concurrent requests to prevent overwhelming the server.
 * This is particularly useful for Supabase which can timeout when too many
 * requests are made in quick succession.
 */

const MAX_CONCURRENT_REQUESTS = 12;

// Requests hang more often than they fail (dropped connections, stalled TLS).
// Without a timeout, MAX_CONCURRENT_REQUESTS hung fetches occupy every slot and
// deadlock all Supabase traffic until reload. 60s is generous for PostgREST
// queries but still bounds the wait. Covers queue-wait + fetch time.
const REQUEST_TIMEOUT_MS = 60 * 1000;

// Bound the pending queue so a flood of requests during an outage can't grow
// without limit — new requests reject immediately once full.
const MAX_PENDING_REQUESTS = 50;

// HTTP statuses that must not have a body per the Fetch spec.
// Passing an empty-string body to new Response() with one of these statuses
// throws on Firefox/Safari (CHURCH-APP-18X/18S/18T/18V/18Y).
const NULL_BODY_STATUSES = new Set([100, 101, 102, 103, 204, 304]);

// Retry config for transient response errors (truncated/corrupted JSON bodies).
// Only GET requests (PostgREST selects) are retried — mutations are not idempotent.
const MAX_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 300;

// Auth requests bypass the queue entirely, but raw fetch has no timeout — a
// stalled connection (half-open TCP, captive portal hijack) would hang the
// token refresh inside auth-js's refreshingDeferred forever. Bound each auth
// request so it errors out (AuthRetryableFetchError) and callers can retry.
const AUTH_REQUEST_TIMEOUT_MS = 20 * 1000;

let activeRequests = 0;
const pendingQueue = [];

/**
 * Checks whether an error is a transient parse/network error that shouldn't
 * be reported to Sentry. Used by data modules to suppress noise from
 * truncated Supabase responses that the retry mechanism didn't fully catch.
 *
 * @param {Error|string|undefined} error - An error object or message string.
 * @returns {boolean} - True if the error is transient.
 */
export const isTransientError = (error) => {
    const msg = typeof error === 'string' ? error : error?.message;
    if (!msg) return false;
    return msg.includes('JSON Parse error')
        || msg.includes('Unexpected end of input')
        // Envoy/PostgREST gateway failures — retryable, not app bugs
        // (CHURCH-APP-1BV).
        || msg.includes('upstream request timeout')
        || msg.includes('upstream connect error')
        // Backpressure rejection from queueRequest — transient once the queue
        // drains (CHURCH-APP-1F6/14J).
        || msg.includes('Request queue is full')
        // Intentional aborts via AbortSignal (nav-away, queue cancel) —
        // Supabase surfaces them as 'AbortError: The operation was aborted.'
        // (CHURCH-APP-1GX).
        || msg.includes('AbortError')
        || msg.includes('operation was aborted');
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Releases a concurrency slot held by an entry and starts the next request.
 * @param {Object} entry - The queue entry holding the slot.
 */
const releaseSlot = (entry) => {
    if (!entry.counted) return;
    entry.counted = false;
    activeRequests--;
    clearTimeout(entry.timeoutId);
    processQueue();
};

/**
 * Processes the next request in the queue if we're under the concurrent limit.
 */
const processQueue = () => {
    while (activeRequests < MAX_CONCURRENT_REQUESTS && pendingQueue.length > 0) {
        const entry = pendingQueue.shift();
        entry.queued = false;
        entry.counted = true;
        activeRequests++;

        // Promise.resolve().then() so a synchronously-throwing requestFn
        // rejects the entry instead of propagating out of the loop.
        Promise.resolve()
            .then(() => entry.execute(entry.signal))
            .then(entry.resolve)
            .catch(entry.reject)
            .finally(() => releaseSlot(entry));
    }
};

/**
 * Queues a request and executes it when a slot is available.
 * The request rejects if it is still unfinished (queued or in-flight) after
 * REQUEST_TIMEOUT_MS — in-flight requests are aborted via the AbortSignal
 * passed to requestFn, and timed-out queued entries are evicted.
 * @param {Function} requestFn - A function (signal) => Promise (the actual request).
 * @returns {Promise} - Resolves/rejects with the request result.
 */
export const queueRequest = (requestFn) => {
    return new Promise((resolve, reject) => {
        if (pendingQueue.length >= MAX_PENDING_REQUESTS) {
            reject(new Error(`Request queue is full (${MAX_PENDING_REQUESTS} pending requests). Try again shortly.`));
            return;
        }

        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const entry = {
            execute: requestFn,
            resolve,
            reject,
            signal: controller ? controller.signal : null,
            queued: true,
            counted: false,
            settled: false,
            timeoutId: null,
        };

        entry.timeoutId = setTimeout(() => {
            if (entry.settled) return;
            entry.settled = true;

            if (entry.queued) {
                const idx = pendingQueue.indexOf(entry);
                if (idx !== -1) pendingQueue.splice(idx, 1);
            } else {
                // In-flight: abort the fetch and free the slot now rather than
                // waiting for a hung request to settle on its own.
                controller?.abort();
                releaseSlot(entry);
            }

            reject(new Error(`Request timed out after ${REQUEST_TIMEOUT_MS / 1000} seconds`));
        }, REQUEST_TIMEOUT_MS);

        pendingQueue.push(entry);
        processQueue();
    });
};

/**
 * Creates a throttled fetch function that respects the request queue.
 * This can be passed to Supabase's global.fetch option.
 *
 * For idempotent GET requests, automatically retries up to MAX_RETRIES times
 * when the response body is truncated/corrupted JSON (transient network errors
 * that manifest as "JSON Parse error: Unexpected end of input" in supabase-js).
 *
 * @param {Function} originalFetch - The original fetch function to wrap.
 * @returns {Function} - A throttled fetch function.
 */
export const createThrottledFetch = (originalFetch = fetch) => {
    // Auth calls (token refresh, sign in/out) bypass the request queue so they
    // can't stall behind data traffic. Two hardening layers on top of raw fetch:
    //
    // 1. Timeout — a hung auth fetch rejects as AuthRetryableFetchError instead
    //    of parking the refresh deferred (and every getSession() joined to it)
    //    forever on a stalled connection.
    // 2. Error-shape normalization — a non-JSON error body or HTTP 429 from an
    //    auth endpoint is NEVER a real GoTrue response (GoTrue always returns
    //    JSON errors and doesn't rate-limit at 4xx for these calls). It means a
    //    middlebox answered (captive portal, WAF, proxy) or a transient throttle.
    //    Left alone, auth-js classifies these AuthUnknownError/AuthApiError —
    //    non-retryable — and _callRefreshToken DESTROYS the persisted session →
    //    SIGNED_OUT → kick to login on a network blip. Re-shaped as 503 they
    //    become AuthRetryableFetchError: session preserved, callers retry.
    const fetchAuthRequest = async (url, options) => {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const callerSignal = options?.signal ?? url?.signal ?? null;
        const abort = () => controller?.abort();

        if (controller && callerSignal) {
            if (callerSignal.aborted) controller.abort();
            else callerSignal.addEventListener('abort', abort, { once: true });
        }
        const timeoutId = controller ? setTimeout(() => controller.abort(), AUTH_REQUEST_TIMEOUT_MS) : null;

        try {
            const response = await originalFetch(
                url,
                controller ? { ...(options ?? {}), signal: controller.signal } : options,
            );

            if (!response.ok) {
                const contentType = response.headers?.get?.('content-type') ?? '';
                if (response.status === 429 || !/json/i.test(contentType)) {
                    return new Response('Auth endpoint unreachable', {
                        status: 503,
                        statusText: 'Service Unavailable',
                        headers: { 'content-type': 'application/json' },
                    });
                }
            }

            return response;
        } finally {
            if (timeoutId) clearTimeout(timeoutId);
            callerSignal?.removeEventListener?.('abort', abort);
        }
    };

    return (url, options) => {
        const urlStr = typeof url === 'string' ? url : url?.url ?? url?.href ?? '';
        if (urlStr.includes('/auth/v1/')) {
            return fetchAuthRequest(url, options);
        }

        return queueRequest(async (queueSignal) => {
            // Merge the queue's timeout signal with any caller-provided signal
            // so either one can abort the underlying fetch.
            const callerSignal = options?.signal ?? url?.signal ?? null;
            let fetchOptions = options;
            let controller = null;
            const abort = () => controller?.abort();

            if (typeof AbortController !== 'undefined') {
                controller = new AbortController();
                if (callerSignal) {
                    if (callerSignal.aborted) controller.abort();
                    else callerSignal.addEventListener('abort', abort, { once: true });
                }
                if (queueSignal) {
                    if (queueSignal.aborted) controller.abort();
                    else queueSignal.addEventListener('abort', abort, { once: true });
                }
                fetchOptions = { ...(options ?? {}), signal: controller.signal };
            }

            try {
                const method = (options?.method || url?.method || 'GET').toUpperCase();
                const canRetry = method === 'GET';
                const maxAttempts = canRetry ? MAX_RETRIES + 1 : 1;

                for (let attempt = 0; attempt < maxAttempts; attempt++) {
                    const response = await originalFetch(url, fetchOptions);
                    // Consume the body immediately and return a JS-level Response.
                    // On Android/Hermes the native Response object can be GC'd before
                    // the Supabase client calls .text(), causing "shared object that
                    // was already released" errors. See CHURCH-APP-17E.
                    // Decode based on content type:
                    // - Textual bodies are read via text() and re-wrapped as a string.
                    //   RN's fetch polyfill decodes ArrayBuffer bodies byte-by-byte
                    //   (Latin-1) in Response.text(), garbling UTF-8 (emoji, curly
                    //   quotes, etc.).
                    // - Binary bodies (e.g. JPEGs from storage.download()) stay as an
                    //   ArrayBuffer — text() would corrupt them with UTF-8 decoding.
                    const contentType = response.headers.get('content-type') ?? '';
                    const isTextual = /json|text\/|xml|html|javascript|urlencoded|svg/i.test(contentType);
                    const body = await (isTextual ? response.text() : response.arrayBuffer());

                    let validatedJson;
                    let validated = false;
                    if (canRetry && attempt < maxAttempts - 1 && /json/i.test(contentType) && !NULL_BODY_STATUSES.has(response.status)) {
                        try {
                            validatedJson = JSON.parse(body);
                            validated = true;
                        } catch {
                            await sleep(RETRY_BASE_DELAY_MS * (attempt + 1));
                            continue;
                        }
                    }

                    const headers = {};
                    response.headers.forEach((value, key) => { headers[key] = value; });
                    const buffered = new Response(NULL_BODY_STATUSES.has(response.status) ? null : body, {
                        status: response.status,
                        statusText: response.statusText,
                        headers,
                    });
                    if (validated) {
                        // postgrest-js consumes text(), not json(). Its versioned
                        // patch reuses this local value instead of parsing twice.
                        Object.defineProperty(buffered, Symbol.for('reach.validated-json'), { value: { value: validatedJson } });
                        buffered.json = async () => {
                            await buffered.text(); // Preserve bodyUsed/one-shot consumption.
                            return validatedJson;
                        };
                    }
                    return buffered;
                }
            } finally {
                if (controller) {
                    callerSignal?.removeEventListener?.('abort', abort);
                    queueSignal?.removeEventListener?.('abort', abort);
                }
            }
        });
    };
};

/**
 * Gets the current queue status for debugging.
 * @returns {Object} - Current active and pending request counts.
 */
export const getQueueStatus = () => ({
    activeRequests,
    pendingRequests: pendingQueue.length,
    maxConcurrent: MAX_CONCURRENT_REQUESTS,
});

export default {
    queueRequest,
    createThrottledFetch,
    getQueueStatus,
    isTransientError,
};
